export default class ApiCommon {
  static frontendDiagnosticsEnabled = false;
  static baseUrl = '/api';
  static frontendLogPath = '/frontend-log';
  static utf8 = 'application/json;charset=UTF-8';
  static Method = {
    GET: 'GET',
    POST: 'POST',
  }
  private static lazy = new Map<string, any>();

  private static isZone665(zone: string | undefined) {
    if (zone === '665') return true;
    if (typeof window === 'undefined') return false;
    return window.location.pathname === '/665' || window.location.pathname.startsWith('/665/');
  }

  private static isTracePath(path: string) {
    return [
      '/sessions',
      '/cost',
      '/foto',
      '/pay',
      '/paymentInfo',
      '/orderStatus',
      '/grz-validation',
      '/grz-input',
    ].some((tracePath) => path.startsWith(tracePath));
  }

  private static isSuspiciousResponse(path: string, status: number | undefined, data: any) {
    if (status && status >= 400) return true;
    if (data?.errorMessage || data?.errorName) return true;

    if (path.startsWith('/cost')) {
      return !data?.result
        || typeof data.result.cost !== 'number'
        || !data.result.timein
        || !data.result.timeout;
    }

    if (path.startsWith('/sessions') || path.startsWith('/paymentInfo') || path.startsWith('/pay') || path.startsWith('/orderStatus')) {
      return !data?.result;
    }

    return false;
  }

  private static trimPayload(payload: any) {
    if (!payload) return payload;

    try {
      const json = JSON.stringify(payload);
      if (json.length <= 2500) return payload;
      return {
        truncated: true,
        length: json.length,
        preview: json.substring(0, 2500),
      };
    } catch (error: any) {
      return {
        stringifyError: error?.message ?? String(error),
      };
    }
  }

  private static sendFrontendLog(params: {
    method: string;
    path: string;
    zone: string | undefined;
    status?: number;
    durationMs: number;
    response?: any;
    request?: any;
    error?: any;
  }) {
    if (!ApiCommon.frontendDiagnosticsEnabled) return;
    if (!ApiCommon.isZone665(params.zone) || !ApiCommon.isTracePath(params.path)) return;

    const payload = {
      source: 'parking-pay-frontend',
      level: params.error ? 'error' : 'warn',
      method: params.method,
      path: params.path,
      zone: params.zone ?? (typeof window !== 'undefined' ? window.location.pathname.split('/')[1] : undefined),
      pageUrl: typeof window !== 'undefined' ? window.location.href : '',
      status: params.status,
      durationMs: params.durationMs,
      response: ApiCommon.trimPayload(params.response),
      request: ApiCommon.trimPayload(params.request),
      error: params.error ? {
        message: params.error?.message ?? String(params.error),
        name: params.error?.name,
      } : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      createdAt: new Date().toISOString(),
    };

    const encodedPayload = encodeURIComponent(JSON.stringify(payload));

    fetch(`${ApiCommon.baseUrl}${ApiCommon.frontendLogPath}?payload=${encodedPayload}`, {
      method: ApiCommon.Method.GET,
      headers: {
        'Content-Type': ApiCommon.utf8,
        'zonenumber': payload.zone ?? '',
      },
      keepalive: true,
    }).catch(() => {
      // Diagnostic logging must not affect the payment flow.
    });
  }

  static logDiagnostic(path: string, zone: string | undefined, response: any) {
    ApiCommon.sendFrontendLog({
      method: 'DIAG',
      path,
      zone,
      durationMs: 0,
      response,
    });
  }

  static async get(path: string, zone: string | undefined) {
    const startedAt = Date.now();
    const option = {
      method: ApiCommon.Method.GET,
      headers: {
        'Content-Type': ApiCommon.utf8,
        'zonenumber': zone ?? '',
      },
    };
    return await fetch(`${ApiCommon.baseUrl}${path}`, option)
      .then(response => {
        const status = response.status;
        return response.json().then(data => {
          if (ApiCommon.isSuspiciousResponse(path, status, data)) {
            ApiCommon.sendFrontendLog({
              method: ApiCommon.Method.GET,
              path,
              zone,
              status,
              durationMs: Date.now() - startedAt,
              response: data,
            });
          }
          return data;
        });
      })
      .catch(error => {
        ApiCommon.sendFrontendLog({
          method: ApiCommon.Method.GET,
          path,
          zone,
          durationMs: Date.now() - startedAt,
          error,
        });
        return error;
      });
  }

  static async getLazy(path: string, zone: string | undefined) {
    const key = `${path}_${zone}`;
    const r = ApiCommon.lazy.get(key);
    if (r) {
      return r;
    }
    return ApiCommon.get(path, zone).then((r) => {
      // @ts-ignore
      ApiCommon.lazy.set(key, r);
      return r;
    });
  }

  static async post(path: string, zone: string | undefined, request: any) {
    const startedAt = Date.now();
    const option = {
      method: ApiCommon.Method.POST,
      headers: {
        'Content-Type': ApiCommon.utf8,
        'zonenumber': zone ?? '',
      },
      body: JSON.stringify(request),
    };
    return await fetch(`${ApiCommon.baseUrl}${path}`, option)
      .then(response => {
        const status = response.status;
        return response.json().then(data => {
          if (ApiCommon.isSuspiciousResponse(path, status, data)) {
            ApiCommon.sendFrontendLog({
              method: ApiCommon.Method.POST,
              path,
              zone,
              status,
              durationMs: Date.now() - startedAt,
              request,
              response: data,
            });
          }
          return data;
        });
      })
      .catch(error => {
        ApiCommon.sendFrontendLog({
          method: ApiCommon.Method.POST,
          path,
          zone,
          durationMs: Date.now() - startedAt,
          request,
          error,
        });
        return error
      });
  }
}
